// Tenant-id client-input guard
// ----------------------------
// The BE rule (per the SME deployment notes + tenant-isolation
// hardening) is "tenantId is never accepted from client input —
// every resolver extracts it server-side from the JWT
// (custom:tenantId claim)." The FE rule is the symmetric
// "tenantId is never sent in a GraphQL variable" — even on
// surfaces where the BE would tolerate it, sending it is a
// programmer-error signal that we should fail loudly on rather
// than silently let through.
//
// This assertion is invoked by every `graphqlOperation` wrapper
// in the codebase (search the repo for callers). Adding a new
// wrapper that doesn't call it is a regression — keep this in
// the same module path so reviewers spot the omission.
//
// Recursive walk: the variables payload is typically
// `{ input: { … } }` for mutations and a flat object for
// queries, but nested shapes (e.g. integrations have
// `{ input: { bindingConfig: { … } } }`) are possible too.
// Catching only top-level wouldn't be enough.

const TENANT_KEY = "tenantId";

export function assertNoTenantIdInVariables(variables: unknown): void {
  if (!variables || typeof variables !== "object") return;
  walk(variables as Record<string, unknown>, "");
}

function walk(node: Record<string, unknown>, path: string): void {
  for (const key of Object.keys(node)) {
    const childPath = path ? `${path}.${key}` : key;
    if (key === TENANT_KEY) {
      throw new Error(
        `assertNoTenantIdInVariables: tenantId found in GraphQL variables at ${childPath}. ` +
          `Tenant identity is sourced from the JWT server-side; never pass it from the client.`,
      );
    }
    const value = node[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      walk(value as Record<string, unknown>, childPath);
    } else if (Array.isArray(value)) {
      value.forEach((entry, index) => {
        if (entry && typeof entry === "object") {
          walk(entry as Record<string, unknown>, `${childPath}[${index}]`);
        }
      });
    }
  }
}
